import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { AgeGroupRow, AuthorRow, CategoryRow } from '../../Settings/lists/singleTableListSupport';
import {
  ageGroupIntervalLabel,
  authorRowSummaryLabel,
  categoryRowSummaryLabel,
} from '../../Settings/lists/singleTableListSupport';
import type { BookFormDraft } from './types';

type BookFormStepDetailsProps = {
  draft: BookFormDraft;
  referenceLoading: boolean;
  sortedAuthors: AuthorRow[];
  sortedCategories: CategoryRow[];
  sortedAgeGroups: AgeGroupRow[];
  setAuthor: (author_id: string) => void;
  toggleCategory: (id: string) => void;
  toggleAgeGroup: (id: string) => void;
  stepNavigation: React.ReactNode;
};

export default function BookFormStepDetails({
  draft,
  referenceLoading,
  sortedAuthors,
  sortedCategories,
  sortedAgeGroups,
  setAuthor,
  toggleCategory,
  toggleAgeGroup,
  stepNavigation,
}: BookFormStepDetailsProps) {
  return (
    <Box>
      {referenceLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} aria-label="Жагсаалт ачааллаж байна" />
        </Box>
      ) : (
        <>
          <FormControl fullWidth margin="dense" required>
            <InputLabel id="book-form-author-label" shrink>
              Зохиолч
            </InputLabel>
            <Select<string>
              labelId="book-form-author-label"
              id="book-form-author"
              label="Зохиолч"
              value={draft.author_id}
              onChange={(e) => setAuthor(e.target.value)}
              displayEmpty
              renderValue={(v) => {
                if (!v) return 'Сонгоно уу';
                const row = sortedAuthors.find((a) => a.id === v);
                return row ? authorRowSummaryLabel(row) : v;
              }}
            >
              <MenuItem value="" disabled>
                <em>Сонгоно уу</em>
              </MenuItem>
              {sortedAuthors.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {authorRowSummaryLabel(a)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Ангилал (олон сонголт)
            </Typography>
            {sortedCategories.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Ангилал байхгүй байна.
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ gap: 1 }}>
                {sortedCategories.map((row) => {
                  const selected = draft.category_ids.includes(row.id);
                  return (
                    <Chip
                      key={row.id}
                      label={categoryRowSummaryLabel(row)}
                      onClick={() => toggleCategory(row.id)}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      clickable
                      aria-pressed={selected}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Насны ангилал (олон сонголт)
            </Typography>
            {sortedAgeGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Насны ангилал байхгүй байна.
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ gap: 1 }}>
                {sortedAgeGroups.map((row) => {
                  const selected = draft.age_group_ids.includes(row.id);
                  return (
                    <Chip
                      key={row.id}
                      label={ageGroupIntervalLabel(row)}
                      onClick={() => toggleAgeGroup(row.id)}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      clickable
                      aria-pressed={selected}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>
        </>
      )}
      {stepNavigation}
    </Box>
  );
}
